import { findBlockedWords } from '@/features/blocked-words/api';

/**
 * TTS variable 30-char limit check on contact content (pasted list or file
 * text). Returns an error string when EVERY data row violates the limit
 * (no valid numbers to upload); null otherwise. Mirrors the wizard's
 * contacts validation exactly.
 */
export function validateTtsVariableLimits(content: string): string | null {
  const lines = content
    .trim()
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 1) return null;

  const header = (lines[0] ?? '').split(',');
  const ttsIndices = header
    .map((h, i) => ({ h: h.trim().toLowerCase(), i }))
    .filter((x) => x.h.startsWith('tts_'))
    .map((x) => x.i);
  if (ttsIndices.length === 0) return null;

  let warnCount = 0;
  for (let r = 1; r < lines.length; r++) {
    const cols = (lines[r] ?? '').split(',');
    let bad = false;
    for (const ti of ttsIndices) {
      if ((cols[ti] ?? '').trim().length > 30) {
        bad = true;
        break;
      }
    }
    if (bad) warnCount++;
  }
  if (warnCount > 0 && warnCount >= lines.length - 1) {
    return `All ${warnCount} numbers have TTS validation issues. No valid numbers to upload.`;
  }
  return null;
}

/**
 * Blocked-word check on TTS variable columns. Returns the error message on
 * the first hit; null when clean. Column 0 is skipped (mobile_number).
 */
export function findBlockedWordInTtsVariables(
  content: string,
  blockedWords: string[],
): string | null {
  if (!content.trim() || blockedWords.length === 0) return null;
  const lines = content
    .trim()
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;

  const header = (lines[0] ?? '').toLowerCase().split(',');
  const ttsIndices = header
    .map((h, i) => (h.trim().startsWith('tts_') ? i : -1))
    .filter((i) => i > 0);
  if (ttsIndices.length === 0) return null;

  for (let r = 1; r < lines.length; r++) {
    const cols = (lines[r] ?? '').split(',');
    for (const ti of ttsIndices) {
      const val = cols[ti]?.trim() ?? '';
      if (val) {
        const matched = findBlockedWords(val, blockedWords);
        if (matched.length > 0) {
          return `TTS variable "${header[ti]?.trim()}" contains blocked word "${matched[0]}" at line ${r + 1}. Remove it to proceed.`;
        }
      }
    }
  }
  return null;
}

/** Convenience: run both content checks; returns the first error or null. */
export function validateContactContent(content: string, blockedWords: string[]): string | null {
  if (!content.trim()) return null;
  return validateTtsVariableLimits(content) ?? findBlockedWordInTtsVariables(content, blockedWords);
}
